/**
 * TypesenseSearch.js
 * Location: src/js/views/TypesenseSearch.js
 */
class TypesenseSearch {
  constructor(el) {
    this.el = el;

    const cfg = window.TypesenseConfig || {};
    this.host = cfg.host || '';
    this.port = cfg.port || '443';
    this.protocol = cfg.protocol || 'https';
    this.searchKey = cfg.searchKey || cfg.search_key || '';
    this.collection = cfg.collection || 'wp_posts';
    this.suggestions = cfg.suggestions || 'wp_query_suggestions';
    this.postTypes = TypesenseSearch._normalizePostTypes(
      cfg.postTypes || cfg.post_types || []
    );
    this.issueFocusAreas = TypesenseSearch._normalizeIssueFocusAreas(
      cfg.issueFocusAreas || cfg.issue_focus_areas || {}
    );
    this.issueFocusAreasUrl =
      cfg.issueFocusAreasUrl || cfg.issue_focus_areas_url || '';
    this.issueFocusAreaRequestKey = '';
    this.isFocusAreaOptionsLoading = false;
    this.allowedFocusAreaLabels = null;
    this.facetOptions = {};
    this.facetOptionsRequestKey = '';
    this.contentScope = TypesenseSearch._contentScopeFromPostTypes(
      this.postTypes
    );
    this.postTypeFilter = TypesenseSearch._postTypeFilter(this.postTypes);

    if (!this.host || !this.searchKey) {
      window.console.warn('[TypesenseSearch] Missing host or searchKey.');
      return;
    }

    // DOM refs
    this.input = this.el.querySelector('#ts-search-input');
    this.dropdown = this.el.querySelector('#ts-suggestions-list');
    this.submitBtn = this.el.querySelector('#ts-search-submit');

    this.activeIdx = -1;
    this.isLearnTemplate = Boolean(
      document.querySelector('.learn-page-template')
    );

    this._bindSearchForm();
    TypesenseSearch._initFilterPanels();
    this._initInstantSearch();
    this._initInlineSearch();
    this._initMobile();
  }

  _debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  static _capitalize(s) {
    return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
  }

  static _slugify(s) {
    return String(s || '')
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  static _normalizeFacetText(value) {
    return TypesenseSearch._cleanFacetLabel(value).toLowerCase();
  }

  static _escapeHtml(value) {
    const el = document.createElement('div');
    el.textContent = String(value || '');

    return el.innerHTML;
  }

  static _decodeHtmlEntities(value) {
    const el = document.createElement('textarea');
    let decoded = String(value || '');

    for (let i = 0; i < 3; i += 1) {
      el.innerHTML = decoded;
      if (el.value === decoded) break;
      decoded = el.value;
    }

    return decoded;
  }

  static _sanitizeHitText(value) {
    return TypesenseSearch._escapeHtml(
      TypesenseSearch._decodeHtmlEntities(value)
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    );
  }

  static _normalizeIssueFocusAreas(issueFocusAreas) {
    return Object.entries(issueFocusAreas).reduce(
      (normalized, [issue, areas]) => ({
        ...normalized,
        [TypesenseSearch._normalizeFacetText(issue)]: (Array.isArray(areas)
          ? areas
          : [areas]
        ).filter(Boolean)
      }),
      {}
    );
  }

  static get LEARN_TYPE_SLUGS() {
    return new Set(['brief', 'report', 'toolkit']);
  }

  static get NEWS_TYPE_SLUGS() {
    return new Set([
      'spotlight',
      'expert-q-a',
      'expert-q-and-a',
      'expert-qa',
      'vitaltalks',
      'vital-talks'
    ]);
  }

  static _normalizePostTypes(postTypes) {
    const allowed = new Set(['resource', 'post', 'page']);
    const values = Array.isArray(postTypes) ? postTypes : [postTypes];

    return values
      .map((postType) => String(postType || '').trim())
      .filter((postType) => allowed.has(postType));
  }

  static _serializeRouteValues(values) {
    return (Array.isArray(values) ? values : [values])
      .map((value) => String(value || '').trim())
      .filter(Boolean)
      .join('|');
  }

  static _parseRouteValues(values) {
    if (Array.isArray(values)) {
      return values.flatMap((value) =>
        TypesenseSearch._parseRouteValues(value)
      );
    }

    const value = String(values || '').trim();
    if (!value) return [];

    // New URLs use pipes. The regex keeps comma-space inside legacy titles.
    return value
      .split(value.includes('|') ? '|' : /,(?!\s)/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  static _contentScopeFromPostTypes(postTypes) {
    if (postTypes.length === 1 && postTypes[0] === 'resource') {
      return 'resources';
    }
    if (postTypes.length === 1 && postTypes[0] === 'post') return 'news';
    return 'all';
  }

  static _postTypeFilter(postTypes) {
    if (!postTypes.length) return '';
    if (postTypes.length === 1) return `post_type:=${postTypes[0]}`;
    return `post_type:=[${postTypes.join(',')}]`;
  }

  static _facetLabel(attribute) {
    const labels = {
      issues: 'Issues',
      media_type: 'Content Type',
      languages: 'Language',
      solutions: 'Solution',
      area_of_focus: 'Area of Focus',
      countries: 'Country',
      city: 'City'
    };

    return (
      labels[attribute] ||
      attribute.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    );
  }

  static _filterUrl(filterName, value, extraParams = {}) {
    const params = new URLSearchParams();
    const filterParams = [
      'q',
      'issue',
      'type',
      'language',
      'solution',
      'areaOfFocus',
      'country',
      'city'
    ];

    filterParams.forEach((param) => {
      params.set(param, param === filterName ? value : '');
    });

    Object.entries(extraParams).forEach(([param, values]) => {
      const cleanValues = (Array.isArray(values) ? values : [values])
        .map((item) => TypesenseSearch._cleanFacetLabel(item))
        .filter(Boolean);

      if (cleanValues.length) {
        params.set(param, TypesenseSearch._serializeRouteValues(cleanValues));
      }
    });

    return `${window.location.pathname}?${params.toString()}#ts-layout`;
  }

  static _learnFilterUrl(filterName, value) {
    return TypesenseSearch._archiveFilterUrl('learn', filterName, value);
  }

  static _archiveFilterUrl(archiveSlug, filterName, value) {
    const params = new URLSearchParams();
    const normalizedArchiveSlug = String(archiveSlug || '').replace(
      /^\/|\/$/g,
      ''
    );
    const path = window.location.pathname
      .replace(/\/search\/?$/, `/${normalizedArchiveSlug}/`)
      .replace(/\/learn\/?$/, `/${normalizedArchiveSlug}/`)
      .replace(/\/news\/?$/, `/${normalizedArchiveSlug}/`);
    const archivePath = path.endsWith(`/${normalizedArchiveSlug}/`)
      ? path
      : `/${normalizedArchiveSlug}/`;

    params.set(filterName, TypesenseSearch._cleanFacetLabel(value));

    return `${archivePath}?${params.toString()}#ts-layout`;
  }

  static _contentTypeFilterUrl(hit, value) {
    const archiveSlug =
      hit.post_type === 'post' || hit.post_type === 'news' ? 'news' : 'learn';

    return TypesenseSearch._archiveFilterUrl(archiveSlug, 'type', value);
  }

  _bindSearchForm() {
    if (!this.input || !this.dropdown) return;

    // Pre-fill from URL ?q= (Useful if someone lands directly on the search page)
    const urlQ = new URLSearchParams(window.location.search).get('q') || '';
    if (urlQ) this.input.value = urlQ;

    // Debounced input handler
    const handleInput = this._debounce(async () => {
      const q = this.input.value.trim();

      // If we are on the results page, sync live typing to InstantSearch
      this._syncInstantSearch(q);

      if (q.length >= 2) {
        await this._fetchSuggestions(q);
      } else {
        this._closeDropdown();
      }
    }, 150);

    this.input.addEventListener('input', handleInput);

    const submit = () => {
      const q = this.input.value.trim();
      if (!q) return;

      if (this.searchInstance) {
        // SCENARIO A: We are already on the search page. Just update the UI dynamically.
        this._syncInstantSearch(q);
        this._closeDropdown();
        requestAnimationFrame(() => {
          const resultsArea = document.getElementById('ts-inline-search');
          if (resultsArea) {
            const offset = 100;
            const top =
              resultsArea.getBoundingClientRect().top + window.scrollY - offset;
            window.scrollTo({ top, behavior: 'smooth' });
          }
        });
      } else {
        // SCENARIO B: We are on the Homepage/About/etc. Redirect to the main search page.
        TypesenseSearch._doSearch(q);
      }
    };

    this.submitBtn?.addEventListener('click', submit);

    this.input.addEventListener('keydown', (e) => {
      const items = this.dropdown.querySelectorAll('[role="option"]');
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.activeIdx = Math.min(this.activeIdx + 1, items.length - 1);
        this._updateActive(items);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.activeIdx = Math.max(this.activeIdx - 1, -1);
        this._updateActive(items);
      } else if (e.key === 'Enter') {
        if (this.activeIdx >= 0 && items[this.activeIdx]) {
          e.preventDefault();
          this._selectSuggestion(
            items[this.activeIdx].dataset.query,
            items[this.activeIdx].dataset.url
          );
        } else {
          e.preventDefault();
          submit(); // Triggers our smart submit handler
        }
      } else if (e.key === 'Escape') {
        this._closeDropdown();
      }
    });

    document.addEventListener('click', (e) => {
      const autocomplete = document.getElementById('ts-autocomplete-wrap');
      if (autocomplete && !autocomplete.contains(e.target)) {
        this._closeDropdown();
      }
    });
  }

  static _doSearch(q) {
    if (!q) return;
    const searchPage = window.TypesenseConfig?.searchPageUrl || '/search/';
    window.location.href = `${searchPage}?q=${encodeURIComponent(q)}`;
  }

  _selectSuggestion(q, url) {
    if (url) {
      window.location.href = url;
      return;
    }
    this.input.value = q;
    this._closeDropdown();
    this._syncInstantSearch(q);
    TypesenseSearch._doSearch(q);
  }

  _updateActive(items) {
    items.forEach((el, i) => {
      const active = i === this.activeIdx;
      el.setAttribute('aria-selected', String(active));
      el.classList.toggle('search-form__suggestion--active', active);
      if (active) this.input.setAttribute('aria-activedescendant', el.id);
    });
  }

  _closeDropdown() {
    this.dropdown.hidden = true;
    this.dropdown.innerHTML = '';
    this.input.setAttribute('aria-expanded', 'false');
    this.activeIdx = -1;
  }

  async _fetchSuggestions(q) {
    const url = `${this.protocol}://${this.host}:${this.port}/multi_search`;

    const payload = {
      searches: [
        {
          collection: this.suggestions,
          q,
          query_by: 'q',
          prefix: true,
          per_page: 6,
          highlight_fields: 'q'
        },
        {
          collection: this.collection,
          q,
          query_by: 'title,excerpt',
          query_by_weights: '3,2',
          per_page: 6,
          highlight_fields: 'title',
          ...(this.postTypeFilter ? { filter_by: this.postTypeFilter } : {})
        }
      ]
    };

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'X-TYPESENSE-API-KEY': this.searchKey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      this._renderDropdown(
        data.results[0]?.hits || [],
        data.results[1]?.hits || []
      );
    } catch (err) {
      window.console.warn('[TypesenseSearch] Autocomplete error:', err);
    }
  }

  _renderDropdown(suggestionHits, previewHits) {
    this.dropdown.innerHTML = '';
    this.activeIdx = -1;

    if (!suggestionHits.length && !previewHits.length) {
      this._closeDropdown();
      return;
    }

    let counter = 0;

    if (suggestionHits.length) {
      this.dropdown.appendChild(TypesenseSearch._groupLabel('Suggestions'));

      suggestionHits.forEach(({ document: doc, highlight }) => {
        const li = document.createElement('li');
        li.id = `ts-opt-${counter}`;
        counter += 1;
        li.role = 'option';
        li.setAttribute('aria-selected', 'false');
        li.dataset.query = doc.q;
        li.className = 'search-form__suggestion search-form__suggestion--query';
        li.innerHTML = `
          <svg class="search-form__suggestion-icon" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" stroke-width="1.4"/>
            <path d="M10.5 10.5l4 4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
          </svg>
          <span class="search-form__suggestion-label">
            ${highlight?.q?.snippet || doc.q}
          </span>
        `;
        li.addEventListener('click', () => this._selectSuggestion(doc.q));
        li.addEventListener('mouseenter', () => {
          this.activeIdx = counter - 1;
          this._updateActive(this.dropdown.querySelectorAll('[role="option"]'));
        });
        this.dropdown.appendChild(li);
      });
    }

    if (previewHits.length) {
      previewHits.forEach(({ document: doc, highlight }) => {
        const li = document.createElement('li');
        li.id = `ts-opt-${counter}`;
        counter += 1;
        li.role = 'option';
        li.setAttribute('aria-selected', 'false');
        li.dataset.query = doc.title;
        li.dataset.url = doc.url || '';
        li.className =
          'search-form__suggestion search-form__suggestion--result';
        li.innerHTML = `
          <div class="search-form__suggestion-body">
            <span class="search-form__suggestion-title">
              ${highlight?.title?.snippet || doc.title}
            </span>
          </div>
        `;
        li.addEventListener('click', () =>
          this._selectSuggestion(doc.title, doc.url)
        );
        li.addEventListener('mouseenter', () => {
          this.activeIdx = counter - 1;
          this._updateActive(this.dropdown.querySelectorAll('[role="option"]'));
        });
        this.dropdown.appendChild(li);
      });
    }

    this.dropdown.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
  }

  static _initFilterPanels() {
    const updateScrollablePanels = () =>
      document
        .querySelectorAll('.ts-filter-panel')
        .forEach((panel) => TypesenseSearch._updatePanelScrollableState(panel));

    document
      .querySelectorAll('.ts-filter-panel__toggle')
      .forEach((btn, index) => {
        btn.addEventListener('click', () => {
          const panel = btn.closest('.ts-filter-panel');
          const isOpen = panel.classList.toggle('ts-filter-panel--open');
          btn.setAttribute('aria-expanded', String(isOpen));
          TypesenseSearch._updatePanelScrollableState(panel);
        });

        // Only open the first panel by default
        if (index === 0) {
          btn.click();
        }
      });

    window.addEventListener('resize', updateScrollablePanels);
    updateScrollablePanels();
  }

  static _groupLabel(text) {
    const li = document.createElement('li');
    li.className = 'search-form__suggestions-group-label';
    li.setAttribute('role', 'presentation');
    li.textContent = text;
    return li;
  }

  _initInstantSearch() {
    if (!document.getElementById('ts-hits')) return;
    if (!window.instantsearch || !window.TypesenseInstantSearchAdapter) return;

    const adapter = new window.TypesenseInstantSearchAdapter({
      server: {
        apiKey: this.searchKey,
        nodes: [{ host: this.host, port: this.port, protocol: this.protocol }],
        connectionTimeoutSeconds: 10,
        numRetries: 3,
        useServerSideSearchCache: true
      },
      additionalSearchParameters: {
        query_by: 'title,excerpt,content',
        query_by_weights: '5,3,1',
        prefix: true,
        num_typos: 2,
        typo_tokens_threshold: 1,
        drop_tokens_threshold: 1,
        highlight_fields: 'title,excerpt',
        highlight_affix_num_tokens: 10,
        facet_by:
          'post_type,issues,media_type,languages,solutions,area_of_focus,countries,city,source_site',
        max_facet_values: 30,
        use_cache: true,
        cache_ttl: 60
      }
    });

    this.searchInstance = window.instantsearch({
      indexName: this.collection,
      searchClient: adapter.searchClient,
      routing: {
        stateMapping: {
          stateToRoute: (uiState) => {
            const idx = uiState[this.collection] || {};
            return {
              q: idx.query || '',
              page: idx.page,
              sortBy: idx.sortBy,
              issue: TypesenseSearch._serializeRouteValues(
                idx.refinementList?.issues || []
              ),
              type: TypesenseSearch._serializeRouteValues(
                idx.refinementList?.media_type || []
              ),
              language: TypesenseSearch._serializeRouteValues(
                idx.refinementList?.languages || []
              ),
              solution: TypesenseSearch._serializeRouteValues(
                idx.refinementList?.solutions || []
              ),
              areaOfFocus: TypesenseSearch._serializeRouteValues(
                idx.refinementList?.area_of_focus || []
              ),
              country: TypesenseSearch._serializeRouteValues(
                idx.refinementList?.countries || []
              ),
              city: TypesenseSearch._serializeRouteValues(
                idx.refinementList?.city || []
              )
            };
          },
          routeToState: (route) => {
            const toArr = (value) => TypesenseSearch._parseRouteValues(value);
            const page = Number.parseInt(route.page, 10);
            const areaOfFocus = toArr(route.areaOfFocus);
            const issues =
              toArr(route.issue).length || !areaOfFocus.length
                ? toArr(route.issue)
                : TypesenseSearch._inferIssuesForFocusAreas(
                    this.issueFocusAreas,
                    areaOfFocus
                  );

            return {
              [this.collection]: {
                query: route.q || '',
                page: Number.isNaN(page) ? undefined : page,
                sortBy: route.sortBy,
                refinementList: {
                  issues,
                  media_type: toArr(route.type),
                  languages: toArr(route.language),
                  solutions: toArr(route.solution),
                  area_of_focus: areaOfFocus,
                  countries: toArr(route.country),
                  city: toArr(route.city)
                }
              }
            };
          }
        }
      }
    });

    const { widgets } = window.instantsearch;

    this.searchInstance.addWidgets([
      widgets.searchBox({
        container: '#ts-search-box-hidden',
        cssClasses: { root: 'ts-hidden-searchbox' }
      }),
      widgets.stats({
        container: '#ts-stats',
        templates: {
          text: ({ nbHits, processingTimeMS }) =>
            nbHits
              ? `<span class="ts-stats__text">${nbHits.toLocaleString()} result${
                  nbHits === 1 ? '' : 's'
                } <span class="ts-stats__time">(${processingTimeMS}ms)</span></span>`
              : ''
        }
      }),
      widgets.hits({
        container: '#ts-hits',
        templates: { item: (hit) => TypesenseSearch._hitTemplate(hit) },
        cssClasses: { list: 'ts-hits-list', item: 'ts-hits-item' },
        transformItems: (items) => {
          document.getElementById('ts-no-results')?.setAttribute('hidden', '');
          return items;
        }
      }),
      widgets.configure({
        hitsPerPage: 20,
        ...(this.postTypeFilter || this.isLearnTemplate
          ? { filters: this.postTypeFilter || 'post_type:=resource' }
          : {})
      }),
      ...TypesenseSearch._makeVirtualFacetWidgets(),
      TypesenseSearch._makePanelClear('#facet-issues-clear', 'issues'),
      TypesenseSearch._makePanelClear('#facet-type-clear', 'media_type'),
      TypesenseSearch._makePanelClear('#facet-language-clear', 'languages'),
      TypesenseSearch._makePanelClear('#facet-solution-clear', 'solutions'),
      TypesenseSearch._makePanelClear(
        '#facet-area-of-focus-clear',
        'area_of_focus'
      ),
      TypesenseSearch._makePanelClear('#facet-country-clear', 'countries'),
      TypesenseSearch._makePanelClear('#facet-city-clear', 'city'),
      widgets.currentRefinements({
        container: '#ts-current-refinements',
        cssClasses: {
          root: 'ts-active-filters',
          list: 'ts-active-filters__list',
          item: 'ts-active-filters__item',
          delete: 'ts-active-filters__delete'
        },
        transformItems: (items) =>
          items.map((item) => ({
            ...item,
            label: TypesenseSearch._facetLabel(item.attribute)
          }))
      }),
      widgets.clearRefinements({
        container: '#ts-sidebar-clear-all',
        templates: {
          resetLabel: `
            <button class="ts-sidebar-clear-all-btn" type="button">
              Clear all
            </button>
          `
        },
        cssClasses: {
          root: 'ts-global-clear',
          disabledButton: 'is-hidden' // InstantSearch automatically hides/disables it if no filters are selected!
        }
      })
    ]);

    // Custom sort using connectSortBy virtual widget
    const { connectSortBy } = window.instantsearch.connectors;

    const sortItems = [
      { value: this.collection, label: 'Relevance' },
      { value: `${this.collection}/sort/date_ts:desc`, label: 'Latest' },
      { value: `${this.collection}/sort/date_ts:asc`, label: 'Oldest' },
      {
        value: `${this.collection}/sort/popularity:desc`,
        label: 'Most popular'
      }
    ];

    const customSortBy = connectSortBy((renderState, isFirstRender) => {
      const container = document.getElementById('ts-sort-by');
      if (!container) return;

      if (isFirstRender) {
        container.innerHTML = `
          <div class="ts-sort">
            <button class="ts-sort__trigger" type="button" aria-haspopup="listbox" aria-expanded="false">
              <span class="ts-sort__label">Relevance</span>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.75 5.7207L8.16667 5.72075" stroke="#121E87" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M1.75 9.2207H5.83333" stroke="#121E87" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M1.75 2.2207H11.0833" stroke="#121E87" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10.7923 12.7207V5.7207M10.7923 12.7207C10.3839 12.7207 9.62069 11.5574 9.33398 11.2624M10.7923 12.7207C11.2008 12.7207 11.9639 11.5574 12.2507 11.2624" stroke="#121E87" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <ul class="ts-sort__dropdown" role="listbox" hidden></ul>
          </div>
        `;

        const trigger = container.querySelector('.ts-sort__trigger');
        const dropdown = container.querySelector('.ts-sort__dropdown');

        trigger.addEventListener('click', () => {
          const isOpen = dropdown.hidden === false;
          dropdown.hidden = isOpen;
          trigger.setAttribute('aria-expanded', String(!isOpen));
        });

        document.addEventListener('click', (e) => {
          if (!container.contains(e.target)) {
            dropdown.hidden = true;
            trigger.setAttribute('aria-expanded', 'false');
          }
        });
      }

      const { currentRefinement, options, refine } = renderState;
      const trigger = container.querySelector('.ts-sort__trigger');
      const label = container.querySelector('.ts-sort__label');
      const dropdown = container.querySelector('.ts-sort__dropdown');

      const current = options.find((o) => o.value === currentRefinement);
      if (label) label.textContent = current?.label || 'Relevance';

      if (dropdown) {
        dropdown.innerHTML = options
          .map(
            (option) => `
          <li
            class="ts-sort__option ${
              option.value === currentRefinement
                ? 'ts-sort__option--selected'
                : ''
            }"
            role="option"
            aria-selected="${option.value === currentRefinement}"
            data-value="${option.value}"
          >
            ${option.label}
          </li>
        `
          )
          .join('');

        dropdown.querySelectorAll('.ts-sort__option').forEach((el) => {
          el.addEventListener('click', () => {
            refine(el.dataset.value);
            dropdown.hidden = true;
            trigger.setAttribute('aria-expanded', 'false');
          });
        });
      }
    });

    this.searchInstance.addWidget(
      customSortBy({ container: '#ts-sort-by', items: sortItems })
    );

    // Custom pagination with ellipsis
    const { connectPagination } = window.instantsearch.connectors;

    let shouldScrollToResults = false;

    const customPagination = connectPagination((renderState) => {
      const container = document.getElementById('ts-pagination');
      if (!container) return;

      const { currentRefinement, nbPages, refine } = renderState;

      if (shouldScrollToResults) {
        shouldScrollToResults = false;
        const resultsArea = document.getElementById('ts-inline-search');
        if (resultsArea) {
          const offset = 100;
          const top =
            resultsArea.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }

      const buildPages = (current, total) => {
        const pages = [];

        if (total <= 3) {
          for (let i = 0; i < total; i += 1) pages.push(i);
          return pages;
        }

        if (current <= 2) {
          const lastLeadingPage = Math.min(current === 2 ? 3 : 2, total - 1);
          for (let i = 0; i <= lastLeadingPage; i += 1) pages.push(i);
          pages.push(total - 1);
        } else if (current >= total - 3) {
          pages.push(0, total - 3, total - 2, total - 1);
        } else {
          pages.push(0, current - 1, current, current + 1, total - 1);
        }

        return [...new Set(pages)]
          .sort((a, b) => a - b)
          .reduce((items, page, index, visiblePages) => {
            if (index > 0 && page - visiblePages[index - 1] > 1) {
              items.push('ellipsis');
            }
            items.push(page);
            return items;
          }, []);
      };

      const pages = buildPages(currentRefinement, nbPages);

      const pageItems = pages
        .map((page) => {
          if (page === 'ellipsis') {
            return `<li class="ts-pagination__item ts-pagination__item--ellipsis">
        <span class="ts-pagination__ellipsis">...</span>
      </li>`;
          }

          const isSelected = page === currentRefinement;
          return `<li class="ts-pagination__item ${
            isSelected ? 'ts-pagination__item--selected' : ''
          }">
      <a class="ts-pagination__link" href="#" data-page="${page}" aria-label="Page ${
            page + 1
          }" ${isSelected ? 'aria-current="page"' : ''}>
        ${page + 1}
      </a>
    </li>`;
        })
        .join('');

      const isPrevDisabled = currentRefinement === 0;
      const isNextDisabled = currentRefinement === nbPages - 1;

      const arrowSvg = `
        <svg class="ts-pagination__arrow" width="22" height="17" viewBox="0 0 22 17" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path class="ts-pagination__arrow-head" d="M12.5 0.710938L20 8.21094L12.5 15.7109" stroke="currentColor" stroke-width="2"/>
          <path class="ts-pagination__arrow-body" d="M20 8.21094H0" stroke="currentColor" stroke-width="2"/>
        </svg>
      `;

      const prevControl = isPrevDisabled
        ? `<button class="ts-pagination__link" type="button" disabled aria-label="Previous page">${arrowSvg}</button>`
        : `<a class="ts-pagination__link" href="#" data-page="${
            currentRefinement - 1
          }" aria-label="Previous page">${arrowSvg}</a>`;

      const nextControl = isNextDisabled
        ? `<button class="ts-pagination__link" type="button" disabled aria-label="Next page">${arrowSvg}</button>`
        : `<a class="ts-pagination__link" href="#" data-page="${
            currentRefinement + 1
          }" aria-label="Next page">${arrowSvg}</a>`;

      container.innerHTML = `
    <ul class="ts-pagination__list">
      <li class="ts-pagination__item ts-pagination__item--prev ${
        isPrevDisabled ? 'ts-pagination__item--disabled' : ''
      }">
        ${prevControl}
      </li>
      ${pageItems}
      <li class="ts-pagination__item ts-pagination__item--next ${
        isNextDisabled ? 'ts-pagination__item--disabled' : ''
      }">
        ${nextControl}
      </li>
    </ul>
  `;

      // Bind clicks
      container.querySelectorAll('[data-page]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          const page = parseInt(el.dataset.page, 10);
          if (Number.isNaN(page) || page < 0 || page >= nbPages) return;
          refine(page);

          setTimeout(() => {
            const resultsArea = document.getElementById('ts-hits');
            if (resultsArea) {
              const offset = 100;
              const scrollTarget =
                resultsArea.getBoundingClientRect().top +
                window.scrollY -
                offset;
              window.scrollTo({ top: scrollTarget, behavior: 'smooth' });
            }
          }, 300);
        });
      });
    });

    this.searchInstance.addWidget(
      customPagination({ container: '#ts-pagination' })
    );

    this.searchInstance.on('render', () => {
      this._syncFacetOptions();
      this._syncDependentFilters();
    });

    this.searchInstance.start();

    const urlQ = new URLSearchParams(window.location.search).get('q') || '';
    if (urlQ) {
      if (this.input) {
        this.input.value = urlQ;
      }
      this._syncInstantSearch(urlQ, { resetPage: false });
    }
  }

  _syncInstantSearch(q, { resetPage = true } = {}) {
    if (!this.searchInstance) return;

    this.searchInstance.setUiState((state) => ({
      ...state,
      [this.collection]: {
        ...(state[this.collection] || {}),
        query: q,
        ...(resetPage ? { page: 1 } : {})
      }
    }));
  }

  _getSearchReturnUrl() {
    const url = new URL(window.location.href);
    const indexState =
      this.searchInstance?.getUiState()?.[this.collection] || {};
    const refinements = indexState.refinementList || {};
    const setListParam = (param, values = []) => {
      if (values.length) {
        url.searchParams.set(
          param,
          TypesenseSearch._serializeRouteValues(values)
        );
      } else {
        url.searchParams.delete(param);
      }
    };

    if (indexState.query) {
      url.searchParams.set('q', indexState.query);
    } else {
      url.searchParams.delete('q');
    }

    if (indexState.page) {
      url.searchParams.set('page', indexState.page);
    } else {
      url.searchParams.delete('page');
    }

    if (indexState.sortBy) {
      url.searchParams.set('sortBy', indexState.sortBy);
    } else {
      url.searchParams.delete('sortBy');
    }

    setListParam('issue', refinements.issues || []);
    setListParam('type', refinements.media_type || []);
    setListParam('language', refinements.languages || []);
    setListParam('solution', refinements.solutions || []);
    setListParam('areaOfFocus', refinements.area_of_focus || []);
    setListParam('country', refinements.countries || []);
    setListParam('city', refinements.city || []);

    return url.toString();
  }

  static _makeVirtualFacetWidgets() {
    const { connectRefinementList } = window.instantsearch.connectors;
    const virtualRefinementList = connectRefinementList(() => {});

    return TypesenseSearch.FACET_REFINEMENTS.map((attribute) =>
      virtualRefinementList({ attribute })
    );
  }

  static _inferIssuesForFocusAreas(issueFocusAreas, focusAreas) {
    const selectedFocusAreas = new Set(
      focusAreas.map((area) => TypesenseSearch._normalizeFacetText(area))
    );

    return Object.entries(issueFocusAreas).reduce((issues, [issue, areas]) => {
      const hasSelectedFocusArea = (areas || []).some((area) =>
        selectedFocusAreas.has(TypesenseSearch._normalizeFacetText(area))
      );

      if (hasSelectedFocusArea) {
        issues.push(TypesenseSearch._cleanFacetLabel(issue));
      }

      return issues;
    }, []);
  }

  static get FACET_REFINEMENTS() {
    return [
      ...TypesenseSearch.DEPENDENT_FACETS.map(({ attribute }) => attribute),
      'area_of_focus'
    ];
  }

  static get DEPENDENT_FACETS() {
    return [
      {
        attribute: 'issues',
        container: '#facet-issues',
        panelId: 'ts-filter-issues'
      },
      {
        attribute: 'solutions',
        container: '#facet-solution',
        panelId: 'ts-filter-solution'
      },
      {
        attribute: 'media_type',
        container: '#facet-type',
        panelId: 'ts-filter-type'
      },
      {
        attribute: 'countries',
        container: '#facet-country',
        panelId: 'ts-filter-country'
      },
      {
        attribute: 'city',
        container: '#facet-city',
        panelId: 'ts-filter-city'
      },
      {
        attribute: 'languages',
        container: '#facet-language',
        panelId: 'ts-filter-language'
      }
    ];
  }

  _getRefinementList() {
    return (
      this.searchInstance?.getUiState()?.[this.collection]?.refinementList || {}
    );
  }

  _getCurrentQuery() {
    return this.searchInstance?.getUiState()?.[this.collection]?.query || '*';
  }

  _syncFacetOptions() {
    if (!this.searchKey || !this.host) return;

    const refinements = this._getRefinementList();
    const query = this._getCurrentQuery();
    const requestKey = JSON.stringify({
      query,
      postTypeFilter: this.postTypeFilter,
      refinements
    });

    if (requestKey === this.facetOptionsRequestKey) return;

    this.facetOptionsRequestKey = requestKey;

    Promise.all(
      TypesenseSearch.DEPENDENT_FACETS.map((facet) =>
        this._fetchFacetOptions(facet.attribute, refinements, query)
      )
    )
      .then((facetGroups) => {
        if (requestKey !== this.facetOptionsRequestKey) return;

        this.facetOptions = facetGroups.reduce(
          (options, { attribute, items }) => ({
            ...options,
            [attribute]: items
          }),
          {}
        );

        TypesenseSearch.DEPENDENT_FACETS.forEach((facet) => {
          this._renderDependentFacet(facet, refinements);
        });
      })
      .catch((err) =>
        window.console.warn('[TypesenseSearch] Facet fetch error:', err)
      );
  }

  _fetchFacetOptions(attribute, refinements, query) {
    const url = new URL(
      `${this.protocol}://${this.host}:${this.port}/collections/${this.collection}/documents/search`
    );
    const filterBy = this._buildFacetFilter(refinements, attribute);

    url.searchParams.set('q', query || '*');
    url.searchParams.set('query_by', 'title,excerpt,content');
    url.searchParams.set('query_by_weights', '5,3,1');
    url.searchParams.set('prefix', 'true');
    url.searchParams.set('num_typos', '2');
    url.searchParams.set('facet_by', attribute);
    url.searchParams.set('max_facet_values', '200');
    url.searchParams.set('per_page', '0');
    url.searchParams.set('use_cache', 'true');
    url.searchParams.set('cache_ttl', '60');

    if (filterBy) {
      url.searchParams.set('filter_by', filterBy);
    }

    return window
      .fetch(url.toString(), {
        headers: {
          'X-TYPESENSE-API-KEY': this.searchKey
        }
      })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        const counts =
          data?.facet_counts?.find((facet) => facet.field_name === attribute)
            ?.counts || [];

        return {
          attribute,
          items: counts.map((item) => ({
            label: TypesenseSearch._cleanFacetLabel(item.value),
            value: TypesenseSearch._cleanFacetLabel(item.value),
            count: item.count
          }))
        };
      });
  }

  _buildFacetFilter(refinements, excludedAttribute = '') {
    const filters = [];

    if (this.postTypeFilter) {
      filters.push(this.postTypeFilter);
    }

    Object.entries(refinements).forEach(([attribute, values]) => {
      if (attribute === excludedAttribute || attribute === 'categories') return;
      if (!Array.isArray(values) || !values.length) return;

      filters.push(TypesenseSearch._facetFilter(attribute, values));
    });

    return filters.filter(Boolean).join(' && ');
  }

  static _facetFilter(attribute, values) {
    const cleanedValues = values
      .map((value) => TypesenseSearch._cleanFacetLabel(value))
      .filter(Boolean)
      .map((value) => `\`${value.replace(/[`\\]/g, '\\$&')}\``);

    if (!cleanedValues.length) return '';

    return `${attribute}:=[${cleanedValues.join(',')}]`;
  }

  static _cleanFacetLabel(value) {
    const el = document.createElement('textarea');
    el.innerHTML = String(value || '');

    return el.value.replace(/\s+/g, ' ').trim();
  }

  _renderDependentFacet(facet, refinements = this._getRefinementList()) {
    const container = document.querySelector(facet.container);
    if (!container) return;

    const selectedValues = refinements[facet.attribute] || [];
    const selectedLookup = new Set(
      selectedValues.map((value) => TypesenseSearch._normalizeFacetText(value))
    );
    const options = TypesenseSearch._filterFacetOptions(
      facet.attribute,
      TypesenseSearch._mergeSelectedFacetOptions(
        this.facetOptions[facet.attribute] || [],
        selectedValues
      )
    );

    container.innerHTML = options.length
      ? `<div class="ts-facet"><ul class="ts-facet__list">${options
          .map((option) => {
            const inputId = `${facet.container.replace(
              '#',
              ''
            )}-${TypesenseSearch._slugify(option.value)}`;
            const safeInputId = TypesenseSearch._escapeHtml(inputId);
            const safeLabel = TypesenseSearch._escapeHtml(option.label);
            const safeValue = TypesenseSearch._escapeHtml(option.value);
            const isChecked = selectedLookup.has(
              TypesenseSearch._normalizeFacetText(option.value)
            );

            return `
              <li class="ts-facet__item${
                isChecked ? ' ts-facet__item--selected' : ''
              }">
                <label class="ts-facet__label" for="${safeInputId}" data-value="${safeValue}">
                  <input class="ts-facet__checkbox" id="${safeInputId}" type="checkbox" value="${safeValue}" ${
              isChecked ? 'checked' : ''
            }>
                  <span>${safeLabel}</span>
                </label>
              </li>
            `;
          })
          .join('')}</ul></div>`
      : '';

    container.querySelectorAll('.ts-facet__label').forEach((label) => {
      label.addEventListener('click', (e) => {
        e.preventDefault();
        TypesenseSearch._toggleFacetItemState(label);
        this._toggleFilter(facet.attribute, label.dataset.value);
      });
    });

    const panel = container.closest('.ts-filter-panel');
    TypesenseSearch._openPanelWhenRefined(panel, selectedValues);
    TypesenseSearch._updatePanelScrollableState(panel);
  }

  static _mergeSelectedFacetOptions(options, selectedValues) {
    const optionLookup = new Set(
      options.map((option) => TypesenseSearch._normalizeFacetText(option.value))
    );
    const selectedOptions = selectedValues
      .filter(
        (value) => !optionLookup.has(TypesenseSearch._normalizeFacetText(value))
      )
      .map((value) => ({
        label: TypesenseSearch._cleanFacetLabel(value),
        value: TypesenseSearch._cleanFacetLabel(value),
        count: 0
      }));

    return [...options, ...selectedOptions]
      .filter((option) => option.label)
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  static _filterFacetOptions(attribute, options) {
    if (attribute !== 'issues') return options;

    return options.filter(
      (option) =>
        TypesenseSearch._normalizeFacetText(option.value) !== 'program'
    );
  }

  static _updatePanelScrollableState(panel) {
    if (!panel) return;

    const body = panel.querySelector('.ts-filter-panel__body');
    if (!body || !panel.classList.contains('ts-filter-panel--open')) {
      panel.classList.remove('ts-filter-panel--scrollable');
      return;
    }

    window.requestAnimationFrame(() => {
      panel.classList.toggle(
        'ts-filter-panel--scrollable',
        body.scrollHeight > body.clientHeight + 1
      );
    });
  }

  static _openPanelWhenRefined(panel, selectedValues = []) {
    if (!panel || !selectedValues.length) return;

    const toggle = panel.querySelector('.ts-filter-panel__toggle');

    panel.classList.add('ts-filter-panel--open');
    toggle?.setAttribute('aria-expanded', 'true');
  }

  _getContentScope(refinements = this._getRefinementList()) {
    const postTypes = refinements.post_type || [];
    const selectedTypeSlugs = (refinements.media_type || []).map((type) =>
      TypesenseSearch._slugify(type)
    );

    if (this.contentScope !== 'all') {
      return this.contentScope;
    }

    if (
      this.isLearnTemplate ||
      postTypes.includes('resource') ||
      selectedTypeSlugs.some((type) =>
        TypesenseSearch.LEARN_TYPE_SLUGS.has(type)
      ) ||
      (refinements.area_of_focus || []).length
    ) {
      return 'resources';
    }

    if (
      postTypes.includes('post') ||
      selectedTypeSlugs.some((type) =>
        TypesenseSearch.NEWS_TYPE_SLUGS.has(type)
      )
    ) {
      return 'news';
    }

    return 'all';
  }

  _syncDependentFilters() {
    const refinements = this._getRefinementList();
    const scope = this._getContentScope(refinements);
    const canShowFocusArea =
      (refinements.issues || []).length > 0 && scope !== 'news';
    let focusAreaLabels = [];

    if (canShowFocusArea) {
      this._syncIssueFocusAreas(refinements.issues || []);
      focusAreaLabels = this._getAllowedFocusAreaLabels(refinements) || [];
    }

    const showFocusArea = canShowFocusArea && focusAreaLabels.length > 0;

    TypesenseSearch._setFilterPanelVisible(
      'ts-filter-area-of-focus',
      showFocusArea,
      true
    );

    if (showFocusArea) {
      this._renderAreaOfFocusFilter(refinements);
    }

    if (!showFocusArea && (refinements.area_of_focus || []).length) {
      this._clearFilter('area_of_focus');
    }

    if (!showFocusArea) {
      this.issueFocusAreaRequestKey = '';
      this.allowedFocusAreaLabels = null;
    }

    if (
      canShowFocusArea &&
      (refinements.area_of_focus || []).length &&
      !this.isFocusAreaOptionsLoading
    ) {
      this._clearUnavailableFocusAreas(refinements);
    }
  }

  _getAllowedFocusAreas(refinements = this._getRefinementList()) {
    const labels = this._getAllowedFocusAreaLabels(refinements);
    if (!labels) return null;

    return new Set(
      labels.map((focusArea) => TypesenseSearch._normalizeFacetText(focusArea))
    );
  }

  _getAllowedFocusAreaLabels(refinements = this._getRefinementList()) {
    if (this.allowedFocusAreaLabels !== null) {
      return this.allowedFocusAreaLabels;
    }

    const selectedIssues = refinements.issues || [];
    if (!selectedIssues.length) return null;

    const labels = selectedIssues.reduce((allowed, issue) => {
      const issueKey = TypesenseSearch._normalizeFacetText(issue);
      (this.issueFocusAreas[issueKey] || []).forEach((focusArea) => {
        allowed.add(focusArea);
      });

      return allowed;
    }, new Set());

    return Array.from(labels);
  }

  _syncIssueFocusAreas(issues) {
    const requestKey = issues
      .map((issue) => TypesenseSearch._normalizeFacetText(issue))
      .sort()
      .join('|');

    if (requestKey === this.issueFocusAreaRequestKey) return;

    this.issueFocusAreaRequestKey = requestKey;
    this.allowedFocusAreaLabels = null;
    this.allowedFocusAreaLabels = this._getAllowedFocusAreaLabels({
      issues,
      area_of_focus: []
    });

    if (!this.issueFocusAreasUrl || !issues.length) return;

    this.isFocusAreaOptionsLoading = true;

    const url = new URL(this.issueFocusAreasUrl, window.location.origin);
    issues.forEach((issue) => url.searchParams.append('issues[]', issue));

    window
      .fetch(url.toString(), {
        credentials: 'same-origin'
      })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (requestKey !== this.issueFocusAreaRequestKey) return;
        if (!data || !Array.isArray(data.areas)) {
          this.isFocusAreaOptionsLoading = false;
          return;
        }

        this.isFocusAreaOptionsLoading = false;
        this.allowedFocusAreaLabels = data.areas;
        this._syncDependentFilters();
      })
      .catch((err) => {
        this.isFocusAreaOptionsLoading = false;
        this._syncDependentFilters();
        window.console.warn(
          '[TypesenseSearch] Area of Focus fetch error:',
          err
        );
      });
  }

  _renderAreaOfFocusFilter(refinements = this._getRefinementList()) {
    const container = document.getElementById('facet-area-of-focus');
    if (!container) return;

    const labels = this._getAllowedFocusAreaLabels(refinements) || [];
    const selectedFocusAreas = refinements.area_of_focus || [];
    const selectedLookup = new Set(
      selectedFocusAreas.map((focusArea) =>
        TypesenseSearch._normalizeFacetText(focusArea)
      )
    );

    container.innerHTML = labels.length
      ? `<div class="ts-facet"><ul class="ts-facet__list">${labels
          .map((label) => {
            const value = TypesenseSearch._cleanFacetLabel(label);
            const inputId = `facet-area-of-focus-${TypesenseSearch._slugify(
              value
            )}`;
            const safeLabel = TypesenseSearch._escapeHtml(value);
            const safeInputId = TypesenseSearch._escapeHtml(inputId);
            const safeValue = TypesenseSearch._escapeHtml(value);
            const isChecked = selectedLookup.has(
              TypesenseSearch._normalizeFacetText(value)
            );

            return `
              <li class="ts-facet__item${
                isChecked ? ' ts-facet__item--selected' : ''
              }">
                <label class="ts-facet__label" for="${safeInputId}" data-value="${safeValue}">
                  <input class="ts-facet__checkbox" id="${safeInputId}" type="checkbox" value="${safeValue}" ${
              isChecked ? 'checked' : ''
            }>
                  <span>${safeLabel}</span>
                </label>
              </li>
            `;
          })
          .join('')}</ul></div>`
      : '';

    container.querySelectorAll('.ts-facet__label').forEach((label) => {
      label.addEventListener('click', (e) => {
        e.preventDefault();
        TypesenseSearch._toggleFacetItemState(label);
        this._toggleFilter('area_of_focus', label.dataset.value);
      });
    });

    const panel = container.closest('.ts-filter-panel');
    TypesenseSearch._openPanelWhenRefined(panel, selectedFocusAreas);
    TypesenseSearch._updatePanelScrollableState(panel);
  }

  static _toggleFacetItemState(label) {
    const item = label.closest('.ts-facet__item');
    const checkbox = label.querySelector('.ts-facet__checkbox');
    const isSelected = !item?.classList.contains('ts-facet__item--selected');

    item?.classList.toggle('ts-facet__item--selected', isSelected);
    if (checkbox) checkbox.checked = isSelected;
  }

  _clearUnavailableFocusAreas(refinements) {
    const allowedFocusAreas = this._getAllowedFocusAreas(refinements);
    if (!allowedFocusAreas) return;

    const selectedFocusAreas = refinements.area_of_focus || [];
    const validFocusAreas = selectedFocusAreas.filter((focusArea) =>
      allowedFocusAreas.has(TypesenseSearch._normalizeFacetText(focusArea))
    );

    if (validFocusAreas.length === selectedFocusAreas.length) return;

    this.searchInstance.setUiState((state) => {
      const indexState = state[this.collection] || {};
      const refinementList = { ...(indexState.refinementList || {}) };

      if (validFocusAreas.length) {
        refinementList.area_of_focus = validFocusAreas;
      } else {
        delete refinementList.area_of_focus;
      }

      return {
        ...state,
        [this.collection]: {
          ...indexState,
          refinementList
        }
      };
    });
  }

  _toggleFilter(attribute, value) {
    if (!this.searchInstance) return;

    this.searchInstance.setUiState((state) => {
      const indexState = state[this.collection] || {};
      const refinementList = { ...(indexState.refinementList || {}) };
      const values = refinementList[attribute] || [];
      const normalizedValue = TypesenseSearch._normalizeFacetText(value);
      const hasValue = values.some(
        (item) => TypesenseSearch._normalizeFacetText(item) === normalizedValue
      );

      refinementList[attribute] = hasValue
        ? values.filter(
            (item) =>
              TypesenseSearch._normalizeFacetText(item) !== normalizedValue
          )
        : [...values, value];

      if (!refinementList[attribute].length) {
        delete refinementList[attribute];
      }

      return {
        ...state,
        [this.collection]: {
          ...indexState,
          page: 1,
          refinementList
        }
      };
    });
  }

  static _setFilterPanelVisible(id, isVisible, openWhenVisible = false) {
    const panel = document.getElementById(id);
    if (!panel) return;

    panel.hidden = !isVisible;
    const toggle = panel.querySelector('.ts-filter-panel__toggle');

    if (isVisible && openWhenVisible) {
      panel.classList.add('ts-filter-panel--open');
      toggle?.setAttribute('aria-expanded', 'true');
      return;
    }

    if (isVisible) return;

    panel.classList.remove('ts-filter-panel--open');
    toggle?.setAttribute('aria-expanded', 'false');
  }

  _clearFilter(attribute) {
    if (!this.searchInstance) return;

    this.searchInstance.setUiState((state) => {
      const indexState = state[this.collection] || {};
      const refinementList = { ...(indexState.refinementList || {}) };
      delete refinementList[attribute];

      return {
        ...state,
        [this.collection]: {
          ...indexState,
          refinementList
        }
      };
    });
  }

  _initInlineSearch() {
    const input = document.getElementById('ts-inline-input');
    const clearBtn = document.getElementById('ts-inline-clear');
    const submitBtn = document.getElementById('ts-inline-submit');

    if (!input) return;

    // Pre-fill from URL ?q=
    const urlQ = new URLSearchParams(window.location.search).get('q') || '';
    input.value = urlQ;

    // Remove the native HTML 'hidden' attribute entirely so class control takes over
    if (clearBtn) {
      clearBtn.removeAttribute('hidden');
      clearBtn.classList.toggle('is-hidden', !urlQ);
    }

    // Sync input → InstantSearch on typing
    const handleInput = this._debounce(() => {
      const q = input.value;

      // FIX: Use classList.toggle consistently instead of .hidden
      if (clearBtn) clearBtn.classList.toggle('is-hidden', !q.trim());

      this._syncInstantSearch(q.trim());
    }, 150);

    input.addEventListener('input', handleInput);

    // Clear button — wipes query
    clearBtn?.addEventListener('click', () => {
      input.value = '';
      if (clearBtn) clearBtn.classList.add('is-hidden'); // Hide on clear
      this._syncInstantSearch('');
      input.focus();
    });

    // Submit button
    submitBtn?.addEventListener('click', () => {
      this._syncInstantSearch(input.value.trim());
    });

    // Enter key
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        this._syncInstantSearch(input.value.trim());
      }
    });

    // Keep inline input in sync when InstantSearch state changes externally
    if (this.searchInstance) {
      this.searchInstance.on('render', () => {
        if (document.activeElement === input) {
          return;
        }

        const state = this.searchInstance.getUiState();
        const q = state[this.collection]?.query || '';
        if (input.value !== q) {
          input.value = q;
          // FIX: Use classList.toggle consistently here as well
          if (clearBtn) clearBtn.classList.toggle('is-hidden', !q);
        }
      });
    }
  }

  _initMobile() {
    const filterBtn = document.getElementById('ts-mobile-filter-btn');
    const sortBtn = document.getElementById('ts-mobile-sort-btn');
    const drawer = document.getElementById('ts-mobile-drawer');
    const drawerClose = document.getElementById('ts-mobile-drawer-close');
    const clearBtn = document.getElementById('ts-mobile-clear');
    const applyBtn = document.getElementById('ts-mobile-apply');
    const sortDrawer = document.getElementById('ts-mobile-sort-drawer');
    const sortClose = document.getElementById('ts-mobile-sort-close');
    const sortApply = document.getElementById('ts-mobile-sort-apply');
    const sortOptions = document.getElementById('ts-mobile-sort-options');
    const overlay = document.getElementById('ts-mobile-overlay');
    const sidebar = document.querySelector('.ts-sidebar');
    const mobileSlot = document.getElementById('ts-mobile-filters-slot');
    const layout = document.querySelector('.ts-layout');

    const moveSidebar = () => {
      if (!sidebar || !mobileSlot || !layout) return;

      if (window.innerWidth <= 767) {
        if (!mobileSlot.contains(sidebar)) mobileSlot.appendChild(sidebar);
      } else if (!layout.contains(sidebar)) {
        layout.insertBefore(sidebar, layout.firstChild);
      }
    };

    moveSidebar();
    window.addEventListener('resize', moveSidebar);

    if (!filterBtn) return;

    const openDrawer = () => {
      drawer?.classList.add('is-open');
      overlay?.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    };

    const closeDrawer = () => {
      drawer?.classList.remove('is-open');
      overlay?.classList.remove('is-visible');
      document.body.style.overflow = '';
    };

    const openSortDrawer = () => {
      sortDrawer?.classList.add('is-open');
      overlay?.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    };

    const closeSortDrawer = () => {
      sortDrawer?.classList.remove('is-open');
      overlay?.classList.remove('is-visible');
      document.body.style.overflow = '';
    };

    filterBtn.addEventListener('click', openDrawer);
    drawerClose?.addEventListener('click', closeDrawer);
    overlay?.addEventListener('click', () => {
      closeDrawer();
      closeSortDrawer();
    });

    // Clear filters
    clearBtn?.addEventListener('click', () => {
      if (this.searchInstance) {
        this.searchInstance.setUiState((state) => ({
          ...state,
          [this.collection]: {
            ...(state[this.collection] || {}),
            refinementList: {}
          }
        }));
      }
    });

    // Apply just closes the drawer
    applyBtn?.addEventListener('click', closeDrawer);

    // Sort drawer
    sortBtn?.addEventListener('click', () => {
      this._renderMobileSortOptions(sortOptions);
      openSortDrawer();
    });
    sortClose?.addEventListener('click', closeSortDrawer);
    sortApply?.addEventListener('click', () => {
      const pending = sortOptions?.dataset.pendingSort;
      if (pending && this.searchInstance) {
        this.searchInstance.setUiState((state) => ({
          ...state,
          [this.collection]: {
            ...(state[this.collection] || {}),
            sortBy: pending
          }
        }));
      }
      closeSortDrawer();
    });
  }

  _renderMobileSortOptions(containerEl) {
    if (!containerEl || !this.searchInstance) return;

    const sortItems = [
      { value: this.collection, label: 'Relevance' },
      { value: `${this.collection}/sort/date_ts:desc`, label: 'Latest' },
      { value: `${this.collection}/sort/date_ts:asc`, label: 'Oldest' },
      {
        value: `${this.collection}/sort/popularity:desc`,
        label: 'Most popular'
      }
    ];

    const currentIndex =
      this.searchInstance.getUiState()[this.collection]?.sortBy ||
      this.collection;

    // Use local ref instead of mutating the parameter directly
    const list = containerEl;
    list.innerHTML = sortItems
      .map(
        (item) => `
      <li class="ts-mobile-sort-drawer__option ${
        item.value === currentIndex
          ? 'ts-mobile-sort-drawer__option--selected'
          : ''
      }"
      data-value="${item.value}">
    <span class="ts-mobile-sort-drawer__radio"></span>
    <span class="ts-mobile-sort-drawer__label">${item.label}</span>
  </li>
    `
      )
      .join('');

    list.querySelectorAll('.ts-mobile-sort-drawer__option').forEach((el) => {
      el.addEventListener('click', () => {
        const found = sortItems.find((i) => i.value === el.dataset.value);
        const label = found?.label || 'Relevance';

        // Update sort label in bar
        const mobileSortLabel = document.getElementById('ts-mobile-sort-label');
        if (mobileSortLabel) mobileSortLabel.textContent = label;

        // Update selected state visually only — don't close yet
        list
          .querySelectorAll('.ts-mobile-sort-drawer__option')
          .forEach((opt) => {
            opt.classList.toggle(
              'ts-mobile-sort-drawer__option--selected',
              opt === el
            );
          });

        // Store selected value for Apply to use
        list.dataset.pendingSort = el.dataset.value;
      });
    });
  }

  static _makeRefinement(container, attribute, extra = {}) {
    return window.instantsearch.widgets.refinementList({
      container,
      attribute,
      limit: 200,
      sortBy: ['name:asc'],
      cssClasses: {
        root: 'ts-facet',
        list: 'ts-facet__list',
        item: 'ts-facet__item',
        label: 'ts-facet__label',
        checkbox: 'ts-facet__checkbox',
        count: 'ts-facet__count',
        showMore: 'ts-facet__show-more',
        selectedItem: 'ts-facet__item--selected'
      },
      templates: {
        showMoreText: ({ isShowingMore }) =>
          isShowingMore ? 'Show less' : 'Show more'
      },
      ...extra
    });
  }

  static _hitTemplate(hit) {
    const title = TypesenseSearch._sanitizeHitText(
      hit._highlightResult?.title?.value || hit.title || ''
    );
    const excerpt = TypesenseSearch._sanitizeHitText(
      hit._highlightResult?.excerpt?.value || hit.excerpt || ''
    );
    const thumb = hit.thumbnail
      ? `<img class="ts-hit__thumb" src="${hit.thumbnail}" alt="" loading="lazy" decoding="async">`
      : '<div class="ts-hit__thumb ts-hit__thumb--placeholder"></div>';

    const mediaClass = hit.thumbnail
      ? 'ts-hit__media'
      : 'ts-hit__media ts-hit__media--empty';

    const getTypeLabel = (h) => {
      if (h.media_type) return TypesenseSearch._capitalize(h.media_type);
      if (h.post_type === 'post') return 'News';
      if (h.post_type === 'resource') return 'Resources';
      return TypesenseSearch._capitalize(h.post_type);
    };

    const getTaxonomyTags = (values, className, filterName, extraParams = {}) =>
      (Array.isArray(values) ? values : [values]).filter(Boolean).map((t) => {
        const label = TypesenseSearch._cleanFacetLabel(t);
        const href = TypesenseSearch._filterUrl(filterName, label, extraParams);
        return `<a class="ts-tag ${className}" href="${href}">${TypesenseSearch._escapeHtml(
          label
        )}</a>`;
      });

    const tags = [
      ...getTaxonomyTags(hit.issues, 'ts-tag--issue', 'issue'),
      ...getTaxonomyTags(hit.area_of_focus, 'ts-tag--focus', 'areaOfFocus', {
        issue: hit.issues
      }),
      ...getTaxonomyTags(hit.solutions, 'ts-tag--solution', 'solution'),
      ...getTaxonomyTags(hit.countries, 'ts-tag--country', 'country'),
      ...getTaxonomyTags(hit.city, 'ts-tag--city', 'city'),
      ...getTaxonomyTags(hit.languages, 'ts-tag--language', 'language')
    ]
      .filter(Boolean)
      .join('');

    const date = hit.date_ts
      ? new Date(hit.date_ts * 1000).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      : '';

    const readingTimeStr = hit.reading_time
      ? `<span class="ts-hit__reading-time">${hit.reading_time} min read</span>`
      : '';

    return `
      <article class="ts-hit">
        <div class="ts-hit__body">
          ${
            getTypeLabel(hit)
              ? `
            <p class="ts-hit__type">
              <a href="${TypesenseSearch._contentTypeFilterUrl(
                hit,
                getTypeLabel(hit)
              )}" class="ts-hit__type-link"><span>${getTypeLabel(
                  hit
                )}</span></a>
            </p>`
              : ''
          }
          <h3 class="ts-hit__title">
            <a href="${hit.url}" class="ts-hit__link"><span>${title}</span></a>
            ${
              hit.source_site && hit.source_site !== window.location.hostname
                ? `<span class="ts-hit__source">${hit.source_site}</span>`
                : ''
            }
          </h3>
          <p class="ts-hit__excerpt">${excerpt}</p>
          ${
            date || readingTimeStr
              ? `
            <div class="ts-hit__meta">
              ${date ? `<time class="ts-hit__date">${date}</time>` : ''}
              ${
                date && readingTimeStr
                  ? '<span class="ts-hit__meta-separator"><svg width="1" height="21" viewBox="0 0 1 21" fill="none" xmlns="http://www.w3.org/2000/svg"><line x1="0.5" y1="2.18561e-08" x2="0.499999" y2="21" stroke="#888EA9"/></svg></span>'
                  : ''
              }
              ${readingTimeStr}
            </div>
          `
              : ''
          }
          ${tags ? `<div class="ts-hit__tags">${tags}</div>` : ''}
        </div>
        <div class="${mediaClass}">
          <a href="${
            hit.url
          }" class="ts-hit__media-link" tabindex="-1" aria-hidden="true">${thumb}</a>
        </div>
      </article>
    `;
  }

  static init() {
    document
      .querySelectorAll('[data-component="TypesenseSearch"]')
      .forEach((el) => {
        const instance = new TypesenseSearch(el);
        return instance;
      });
  }

  static _makePanelClear(container, attribute) {
    return window.instantsearch.widgets.clearRefinements({
      container,
      includedAttributes: [attribute],
      cssClasses: {
        root: 'ts-facet-clear',
        button: 'ts-facet-clear__btn',
        disabledButton: 'ts-facet-clear__btn--disabled'
      },
      templates: {
        resetLabel: 'Clear'
      }
    });
  }
}

export default TypesenseSearch;
